import PropTypes from 'prop-types'
import clsx from 'clsx'

import styles from './style.module.scss'

/**
 * Wer die Lernstrecke abgeschlossen hat — und wie viele nötig sind.
 *
 * **Eine Komponente für drei Rollen.** Schüler:innen, Lehrkraft und Verwaltung
 * hatten je eine eigene Personenliste, aber keine sagte, wie viele Abschlüsse
 * überhaupt verlangt werden. Solange das „alle" war, ließ sich das noch raten;
 * seit die Zahl in den Einstellungen steht und je Unternehmensform verschieden
 * ist, geht das nicht mehr — eine Klasse mit halber Quote sah aus wie eine, bei
 * der die Hälfte säumig ist.
 *
 * Gezeigt wird genau das, was für die Frage „woran hängt es?" nötig ist:
 * **wie viele nötig sind, wie viele erfüllt haben, und wer noch offen ist.**
 * Die Antworten der Quizze stehen hier nicht.
 *
 * Die Zahlen kommen aus `state` (`courseRequired`, `courseDone`,
 * `courseMissing`) — gerechnet wird im Backend
 * (`services/concept/courseRequirement.js`), damit Sperre und Anzeige nicht
 * auseinanderlaufen. Fehlen sie, zeigt die Komponente nichts: Eine erfundene
 * Zahl wäre schlimmer als keine.
 */
export default function CourseStanding({ state, students, config, className, ohneListe = false }) {
	const noetig = state?.courseRequired
	if (typeof noetig !== 'number') return null

	const kapitel = config?.chapters || 10
	const erfuellt = state?.courseDone ?? 0
	const fehlen = state?.courseMissing ?? 0
	const alle = students?.length || 0

	// Wer noch nicht fertig ist — nach Name sortiert kommt die Liste aus dem
	// Backend, hier bleibt sie in derselben Reihenfolge.
	const offen = (students || []).filter(student => !student.exempt && (student.chaptersPassed || 0) < kapitel)

	return (
		<div className={clsx(styles.root, fehlen === 0 && styles.ready, className)}>
			<p className={styles.head}>
				{noetig === 0 ? (
					<>
						<b>Die Lernstrecke ist keine Voraussetzung.</b> Für diese Unternehmensform hält sie das Konzept
						nicht zurück — bearbeiten lässt sie sich trotzdem.
					</>
				) : (
					<>
						<b>
							{erfuellt} von {noetig} nötigen Abschlüssen
						</b>
						{alle > noetig && <> — die Junior Company hat {alle} Mitarbeiter:innen</>}
						{'. '}
						{fehlen === 0
							? 'Die Voraussetzung ist erfüllt.'
							: `Es ${
									fehlen === 1 ? 'fehlt noch 1 Person' : `fehlen noch ${fehlen} Personen`
							  }, bevor das Konzept zur Bewertung abgeschlossen werden kann.`}
					</>
				)}
			</p>

			{/*
			 * Die Namensliste nur, wenn tatsächlich noch jemand offen ist — und
			 * nur dort, wo die Ansicht nicht ohnehin schon alle Namen zeigt
			 * (`ohneListe`). Zweimal dieselbe Liste untereinander beantwortet
			 * keine Frage zweimal, sie macht die Seite nur länger.
			 */}
			{!ohneListe && fehlen > 0 && offen.length > 0 && (
				<p className={styles.names}>
					Noch offen bei: <b>{offen.map(student => student.name || 'unbekannt').join(', ')}</b>
					{offen.length > fehlen && (
						<>
							{' '}
							— es müssen aber nicht alle davon abschließen, sondern nur noch{' '}
							{fehlen === 1 ? 'eine Person' : `${fehlen} Personen`}.
						</>
					)}
				</p>
			)}
		</div>
	)
}

CourseStanding.propTypes = {
	state: PropTypes.object,
	students: PropTypes.array,
	config: PropTypes.object,
	className: PropTypes.string,
	ohneListe: PropTypes.bool
}
